/**
 * @description Portal payment form — gateway-aware wrapper.
 * Uses CardEntry (HPF or legacy) based on the active gateway.
 *
 * HPF path: Card data captured in Elavon iFrames — PCI SAQ-A compliant.
 * Legacy path: Tokenizes locally via PaymentTokenizationService (existing flow).
 *
 * @dependencies
 * - CardEntry: Gateway-aware card entry wrapper
 * - PaymentTokenizationService: Legacy card tokenization (non-HPF fallback)
 */

import { useState, useEffect } from 'react';
import PaymentTokenizationService from '../../services/PaymentTokenizationService';
import { formatCurrency } from '../../utils/amountUtils';
import CardEntry from '../shared/CardEntry';
import portalService from '../../services/portalService';

function PortalPaymentForm({ onSubmit, amount, processing = false, error = null, customerPublicId }) {
  const [paymentConfigured, setPaymentConfigured] = useState(null);

  useEffect(() => {
    if (!customerPublicId) return;
    portalService.getPaymentStatus(customerPublicId)
      .then(data => setPaymentConfigured(data.configured))
      .catch(() => setPaymentConfigured(false));
  }, [customerPublicId]);
  // Handle form submission — gateway-aware
  const handleCardFormSubmit = async (formData) => {
    if (formData.type === 'hostedCard') {
      // ── HPF Path: Card captured by Elavon iFrames (PCI SAQ-A) ──
      // Card data NEVER touched our DOM or JS
      // Surcharge no longer comes from the HPF SDK — it's checked server-side
      // via vault-and-check-surcharge after this submission completes
      onSubmit({
        type: 'hostedCard',
        hostedCardId: formData.hostedCardId,
        token: formData.hostedCardId,
        method: 'hostedCard',
        sessionId: formData.sessionId,
        last4: formData.last4,
        cardBrand: formData.brand,
        cardName: formData.holderName,
        billingAddress: formData.billingAddress,
        billingCity: formData.billingCity,
        billingState: formData.billingState,
        billingZip: formData.billingZip
      });
    } else {
      // ── Legacy Path: No gateway configured — tokenize locally ──
      try {
        const tokenResult = await PaymentTokenizationService.tokenize({
          cardNumber: formData.cardNumber,
          cvv: formData.cvv,
          expiryMonth: formData.expiryMonth,
          expiryYear: formData.expiryYear,
          cardName: formData.cardholderName
        });

        onSubmit({
          token: tokenResult.token,
          method: tokenResult.method,
          last4: tokenResult.last4,
          cardBrand: tokenResult.cardBrand,
          expiryMonth: tokenResult.expiryMonth,
          expiryYear: tokenResult.expiryYear,
          cardName: formData.cardholderName,
          billingAddress: formData.billingAddress,
          billingCity: formData.billingCity,
          billingState: formData.billingState,
          billingZip: formData.billingZip,
        });
      } catch (tokenError) {
        console.error('Payment tokenization failed:', tokenError.message);
        throw tokenError;
      }
    }
  };

  if (paymentConfigured === false) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-700">Online payments are not currently available.</p>
        <p className="text-xs text-gray-500 mt-1">Please contact the merchant for payment arrangements.</p>
      </div>
    );
  }

  return (
    <CardEntry
      onSubmit={handleCardFormSubmit}
      processing={processing}
      error={error}
      submitButtonText={`Pay ${formatCurrency(amount)}`}
      amount={Math.round((amount || 0) * 100)}
      currency="USD"
      description="Invoice payment"
      theme="portal"
      customerPublicId={customerPublicId}
    />
  );
}

export default PortalPaymentForm;